import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import {
  Button,
  Container,
  Box,
  Paper,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Table,
  MenuItem,
  Skeleton,
} from '@mui/material';
import InfiniteScroll from 'react-infinite-scroller';

import { useDeleteCategoryMutation } from 'src/api/categories/deleteCategory';
import { useGetInfiniteCategoriesQuery } from 'src/api/categories/getCategories';
import DeleteDialog from 'src/components/pickem-admin/DeleteDialog';
import CategoriesDialog from 'src/components/Categories/categories.modal';
import Iconify from 'src/components/iconify';
import { useTableColumns } from 'src/hooks/useTableColumns';
import { useTimezoneStore } from 'src/store/timezoneStore';
import { useGlobalFiltersStore } from 'src/store/globalFilters.store';
import { dateUtils } from 'src/utils/date';

export default function Categories() {
  const { timezone } = useTimezoneStore();
  const { languageCode } = useGlobalFiltersStore();

  const [open, setOpen] = useState(false);
  const [openId, setOpenId] = useState<number>(-1);

  const [addCategory, setAddCategory] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState<number>(-1);
  const [deleteCategoryId, setDeleteCategoryId] = useState<number>(-1);

  // API
  const {
    data: categories = [],
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetInfiniteCategoriesQuery({
    languageCode,
  });
  const { mutateAsync: triggerDelete, isPending: isDeleteLoading } =
    useDeleteCategoryMutation();

  const deleteCategoryHandler = async (id: number) => {
    setOpen(false);
    setOpenId(-1);

    await triggerDelete(id);
  };

  const closeDeleteModal = () => {
    setOpen(false);
    setOpenId(-1);
    setDeleteCategoryId(-1);
  };

  const closeSaveEditModal = () => {
    setOpen(false);
    setOpenId(-1);
    setAddCategory(false);
    setEditCategoryId(-1);
  };

  const { categoriesColumns } = useTableColumns();

  return (
    <>
      <Helmet>
        <title>Categories</title>
      </Helmet>
      <Container>
        <Box>
          <Button
            variant='contained'
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => setAddCategory(true)}
          >
            New Category
          </Button>
          <Button
            disabled
            variant='contained'
            style={{ marginLeft: '20px' }}
            startIcon={<Iconify icon='eva:plus-fill' />}
            onClick={() => setAddCategory(true)}
          >
            Create Batch
          </Button>
        </Box>
        <Box sx={{ width: '100%', mt: 3 }}>
          <InfiniteScroll
            pageStart={0}
            loadMore={() => fetchNextPage()}
            hasMore={hasNextPage}
            loader={
              <div className='loader' key='loader'>
                Loading ...
              </div>
            }
          >
            {isLoading ? (
              <Skeleton width='100%' height={120} />
            ) : (
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label='categories table'>
                  <TableHead>
                    <TableRow>
                      {categoriesColumns.map((column) => (
                        <TableCell key={column.id} align={column.align}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categories?.map((row, index) => (
                      <TableRow
                        key={row.id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        {categoriesColumns.map((header) => {
                          switch (header.id) {
                            case 'createdAt':
                              return (
                                <TableCell
                                  component='th'
                                  scope='row'
                                  align={header.align}
                                >
                                  {dateUtils.formatForDisplay(
                                    row.createdAt,
                                    timezone
                                  )}
                                </TableCell>
                              );
                            case 'explanation':
                              return (
                                <TableCell
                                  component='th'
                                  scope='row'
                                  align={header.align}
                                >
                                  {row.explanation?.length > 20
                                    ? row.explanation?.slice(0, 20) + '...'
                                    : row.explanation}
                                </TableCell>
                              );
                            case 'actions':
                              return (
                                <TableCell
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                  }}
                                >
                                  <MenuItem
                                    onClick={() => {
                                      setEditCategoryId(row.id);
                                      setOpenId(index);
                                    }}
                                  >
                                    <Iconify icon={'eva:edit-outline'} />
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() => {
                                      setDeleteCategoryId(row.id);
                                      setOpen(true);
                                    }}
                                  >
                                    <Iconify icon={'eva:trash-2-outline'} />
                                  </MenuItem>
                                </TableCell>
                              );
                            default:
                              return (
                                <TableCell key={header.id} align={header.align}>
                                  {row[header.id as keyof typeof row]}
                                </TableCell>
                              );
                          }
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </InfiniteScroll>
        </Box>
      </Container>

      {(addCategory || editCategoryId !== -1) && (
        <CategoriesDialog
          open={addCategory || editCategoryId !== -1}
          category={categories?.[openId]}
          onClose={closeSaveEditModal}
        />
      )}

      {deleteCategoryId !== -1 && (
        <DeleteDialog
          openConfirm={open}
          type='category'
          index={deleteCategoryId}
          setOpenConfirm={closeDeleteModal}
          deleteGameHandler={deleteCategoryHandler}
          isLoading={isDeleteLoading}
        />
      )}
    </>
  );
}
